/**
 * ReceiptUploadModal Component
 * 
 * This component provides a modal for uploading receipt files for expenses.
 * It handles file validation, upload to Firebase Storage, and updates expense records.
 * 
 * Features:
 * - File upload with size and type validation
 * - Firebase Storage integration
 * - Progress indication during upload
 * - Error handling and user feedback
 * - Full internationalization support (Arabic/English)
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { storage } from '../../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { expensesService } from '../../../services/accounting/expenses';
import LoadingSpinner from '../../ui/LoadingSpinner';
import toast from 'react-hot-toast';
import { compressImage, needsCompression, getOptimalCompressionSettings } from '../../../utils/imageCompression';

const ReceiptUploadModal = ({ expense, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [originalFile, setOriginalFile] = useState(null);
  const [compressionInfo, setCompressionInfo] = useState(null);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size limit (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('expenses.error.file_too_large'));
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('expenses.error.invalid_file_type'));
      return;
    }

    setOriginalFile(file);
    setCompressionInfo(null);

    // Compress image if needed
    if (needsCompression(file)) {
      try {
        setLoading(true);
        const settings = getOptimalCompressionSettings(file);
        const compressedFile = await compressImage(file, settings);
        
        setSelectedFile(compressedFile);
        setCompressionInfo({
          originalSize: file.size,
          compressedSize: compressedFile.size,
          reduction: ((file.size - compressedFile.size) / file.size * 100).toFixed(1)
        });
        
        toast.success(
          `Image compressed: ${(file.size / 1024 / 1024).toFixed(1)}MB → ${(compressedFile.size / 1024 / 1024).toFixed(1)}MB`
        );
      } catch (error) {
        console.error('Compression failed:', error);
        toast.error('Image compression failed, using original file');
        setSelectedFile(file);
      } finally {
        setLoading(false);
      }
    } else {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error(t('expenses.error.no_file_selected'));
      return;
    }

    setLoading(true);
    try {
      const receiptRef = ref(storage, `receipts/${currentCompany.id}/${expense.id}/${selectedFile.name}`);
      await uploadBytes(receiptRef, selectedFile);
      const receiptUrl = await getDownloadURL(receiptRef);
      
      await expensesService.updateExpense(expense.id, {
        receiptAttached: true,
        receiptUrl: receiptUrl,
        receiptFileName: selectedFile.name
      });
      
      toast.success(t('expenses.success.receipt_uploaded'));
      onSuccess();
    } catch (error) {
      console.error('Error uploading receipt:', error);
      toast.error(t('expenses.error.upload_failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">{t('expenses.upload_receipt_for', 'Upload receipt for')} {expense.description}</h3>
          
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">{t('expenses.select_file', 'Select File')}</label>
            <input
              type="file"
              accept="image/*,application/pdf"
              onChange={handleFileChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-1">{t('expenses.upload_help', 'Accepted formats: JPG, PNG, PDF. Max size: 10MB')}</p>
            
            {/* Show compression info */}
            {compressionInfo && (
              <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                <p className="text-xs text-green-700">
                  ✅ Image compressed by {compressionInfo.reduction}% 
                  ({(compressionInfo.originalSize / 1024 / 1024).toFixed(1)}MB → {(compressionInfo.compressedSize / 1024 / 1024).toFixed(1)}MB)
                </p>
              </div>
            )}
            
            {/* Show file info */}
            {selectedFile && (
              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-xs text-blue-700">
                  📎 {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(1)}MB)
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-2">
            <button onClick={onClose} className="btn btn-outline">
              {t('common.cancel', t('common.cancel'))}
            </button>
            <button
              onClick={handleUpload}
              disabled={!selectedFile || loading}
              className="btn btn-primary"
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" />
                  {t('expenses.uploading', 'Uploading...')}
                </>
              ) : (
                t('expenses.upload', 'Upload')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptUploadModal;
